import React, { useState, useEffect, useRef } from 'react';
import { deliveryAPI } from '../api';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import printingLogo from '../printing-logo.jpg';
import { Printer, FileDown, Search, Calendar, Truck, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';

function formatCurrency(val) {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val || 0);
}

function formatDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function Printing() {
  const [groups, setGroups] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [printData, setPrintData] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);
  const printSheetRef = useRef(null);

  useEffect(() => { loadGroups(); }, [search, dateFrom, dateTo]);

  async function loadGroups(page = 1) {
    setLoading(true);
    try {
      const res = await axios.get('/api/printing/groups', {
        params: { search, dateFrom, dateTo, page, limit: 15 }
      });
      setGroups(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      console.error('Load groups error:', err);
    }
    setLoading(false);
  }

  function toDateString(d) {
    if (!d) return '';
    // Extract date directly from string to avoid timezone conversion issues
    // e.g. "2026-10-04T16:00:00.000Z" -> "2026-10-04"
    if (typeof d === 'string' && d.includes('T')) {
      return d.slice(0, 10);
    }
    const dt = new Date(d);
    return dt.toISOString().slice(0, 10);
  }

  async function handlePrint(date, plate) {
    setPrintLoading(true);
    const dateStr = toDateString(date);
    console.log('Printing:', { date: dateStr, plate });
    try {
      const res = await axios.get('/api/printing/detail', {
        params: { date: dateStr, plate }
      });
      setPrintData(res.data);
    } catch (err) {
      alert('Error loading print data: ' + (err.response?.data?.error || err.message));
    }
    setPrintLoading(false);
  }

  async function handleDownloadPDF() {
    if (!printSheetRef.current) return;
    const header = printData.header;
    const fileName = `YSU_Delivery_Log_${header.plate_number.replace(/\s+/g, '_')}_${toDateString(header.dispatch_date)}.pdf`;

    const opt = {
      margin: [10, 10, 10, 10],
      filename: fileName,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    try {
      await html2pdf().set(opt).from(printSheetRef.current).save();
    } catch (err) {
      console.error('PDF generation error:', err);
      alert('Failed to generate PDF');
    }
  }

  return (
    <div>
      {/* Print Preview Modal */}
      {printData && (
        <div className="print-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', zIndex: 3000, overflow: 'auto', padding: 'clamp(8px, 2vw, 20px)'
        }}>
          <div className="print-controls" style={{
            position: 'sticky', top: 0, background: 'white', padding: '12px 20px',
            borderRadius: 12, marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)', zIndex: 10
          }}>
            <button className="btn btn-secondary" onClick={() => setPrintData(null)}>
              <ArrowLeft size={16} /> Back
            </button>
            <button className="btn btn-primary" onClick={() => window.print()}>
              <Printer size={16} /> Print
            </button>
            <button className="btn btn-primary" onClick={handleDownloadPDF} style={{ background: '#dc2626' }}>
              <FileDown size={16} /> Download PDF
            </button>
          </div>

          {/* YSU Delivery Log Format */}
          <div ref={printSheetRef} className="print-sheet" style={{
            background: 'white', maxWidth: 800, margin: '0 auto', padding: 'clamp(12px, 3vw, 30px) clamp(12px, 4vw, 40px)',
            fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#000'
          }}>
            {/* Header */}
            <div style={{ position: 'relative', textAlign: 'center', marginBottom: 8, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src={printingLogo} alt="YSU Marketing Corp. logo" width={96} height={40} style={{ position: 'absolute', right: 0, top: 2, objectFit: 'contain' }} />
              <div>
                <h2 style={{ fontSize: 22, fontWeight: 'bold', margin: 0, letterSpacing: 2 }}>YSU DELIVERY LOG</h2>
                <p style={{ fontSize: 10, color: '#666', margin: '2px 0 0' }}>YSU Marketing Corp.</p>
              </div>
            </div>

            {/* Info Grid */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12, fontSize: 12 }}>
              <tbody>
                <tr>
                  <td style={{ padding: '4px 8px', fontWeight: 'bold', width: 80 }}>DATE:</td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #000', width: 180 }}>
                    {formatDate(printData.header.dispatch_date)}
                  </td>
                  <td style={{ padding: '4px 8px', fontWeight: 'bold', width: 80 }}>PLATE #:</td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #000', width: 180 }}>
                    {printData.header.plate_number}
                  </td>
                  <td rowSpan={5} style={{
                    padding: '8px', textAlign: 'center', verticalAlign: 'middle',
                    border: '1px solid #ccc', borderRadius: 4, width: 160
                  }}>
                    <div style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 4 }}>TOTAL AMOUNT</div>
                    <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1e40af' }}>
                      {formatCurrency(printData.header.total_amount)}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: '4px 8px', fontWeight: 'bold' }}>DRIVER:</td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #000' }}>{printData.header.driver || ''}</td>
                  <td style={{ padding: '4px 8px', fontWeight: 'bold' }}>SIGNATURE:</td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #000', height: 24 }}></td>
                </tr>
                <tr>
                  <td style={{ padding: '4px 8px', fontWeight: 'bold' }}>HELPER 1:</td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #000' }}>{printData.header.helper_1 || ''}</td>
                  <td style={{ padding: '4px 8px', fontWeight: 'bold' }}>SIGNATURE:</td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #000', height: 24 }}></td>
                </tr>
                <tr>
                  <td style={{ padding: '4px 8px', fontWeight: 'bold' }}>HELPER 2:</td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #000' }}>{printData.header.helper_2 || ''}</td>
                  <td style={{ padding: '4px 8px', fontWeight: 'bold' }}>SIGNATURE:</td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #000', height: 24 }}></td>
                </tr>
                <tr>
                  <td style={{ padding: '4px 8px', fontWeight: 'bold' }}>CHECKER:</td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #000' }}>{printData.header.checker_name || printData.header.checker || ''}</td>
                  <td style={{ padding: '4px 8px', fontWeight: 'bold' }}>SIGNATURE:</td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #000', height: 24 }}></td>
                </tr>
                <tr>
                  <td style={{ padding: '4px 8px', fontWeight: 'bold' }}>DESTINATION:</td>
                  <td colSpan={2} style={{ padding: '4px 8px', borderBottom: '1px solid #000' }}>{printData.header.destination || ''}</td>
                  <td style={{ padding: '4px 8px', fontWeight: 'bold' }}>REVIEWED BY:</td>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #000', height: 24 }}></td>
                </tr>
              </tbody>
            </table>

            {/* Pull-Out Slip Section */}
            {printData.pullOuts && printData.pullOuts.length > 0 && (
              <>
                <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 13, margin: '16px 0 8px', textDecoration: 'underline' }}>
                  PULL-OUT SLIP
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: '#e5e7eb' }}>
                      <th style={{ border: '1px solid #999', padding: '6px 8px', width: 50, textAlign: 'center' }}>NO.</th>
                      <th style={{ border: '1px solid #999', padding: '6px 8px' }}>DOCUMENT #</th>
                      <th style={{ border: '1px solid #999', padding: '6px 8px' }}>CUSTOMER</th>
                      <th style={{ border: '1px solid #999', padding: '6px 8px', width: 120, textAlign: 'right' }}>AMOUNT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {printData.pullOuts.map((p, i) => (
                      <tr key={i}>
                        <td style={{ border: '1px solid #999', padding: '5px 8px', textAlign: 'center' }}>{p.no}</td>
                        <td style={{ border: '1px solid #999', padding: '5px 8px' }}>{p.document_no}</td>
                        <td style={{ border: '1px solid #999', padding: '5px 8px' }}>{p.customer}</td>
                        <td style={{ border: '1px solid #999', padding: '5px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* Delivery Items Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ background: '#e5e7eb' }}>
                  <th style={{ border: '1px solid #999', padding: '6px 8px', width: 50, textAlign: 'center' }}>NO.</th>
                  <th style={{ border: '1px solid #999', padding: '6px 8px', width: 100 }}>INVOICE #</th>
                  <th style={{ border: '1px solid #999', padding: '6px 8px' }}>CUSTOMER</th>
                  <th style={{ border: '1px solid #999', padding: '6px 8px', width: 120, textAlign: 'right' }}>AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                {printData.items.map((item, i) => (
                  <tr key={i} style={i % 2 === 0 ? { background: '#f9fafb' } : {}}>
                    <td style={{ border: '1px solid #999', padding: '5px 8px', textAlign: 'center' }}>{item.no}</td>
                    <td style={{ border: '1px solid #999', padding: '5px 8px' }}>{item.invoice_number}</td>
                    <td style={{ border: '1px solid #999', padding: '5px 8px' }}>{item.customer_name}</td>
                    <td style={{ border: '1px solid #999', padding: '5px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(item.amount)}</td>
                  </tr>
                ))}
                <tr style={{ background: '#e5e7eb', fontWeight: 'bold' }}>
                  <td colSpan={3} style={{ border: '1px solid #999', padding: '6px 8px', textAlign: 'right' }}>TOTAL:</td>
                  <td style={{ border: '1px solid #999', padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatCurrency(printData.header.total_amount)}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Footer signatures */}
            <div style={{ marginTop: 40, display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ borderTop: '1px solid #000', width: 180, paddingTop: 4 }}>Prepared by</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ borderTop: '1px solid #000', width: 180, paddingTop: 4 }}>Received by</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Print Selection Page */}
      <div className="table-container">
        <div className="table-header table-title-row">
          <h3>For Printing - YSU Delivery Log ({pagination.total.toLocaleString()} groups)</h3>
        </div>

        <div className="table-filters-row">
          <div className="table-search-wrap">
            <Search size={16} className="table-search-icon" />
            <input
              className="search-input table-search-input"
              placeholder="Search plate number or driver..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <label className="table-date-field">
            <span>From</span>
            <input type="date" className="filter-select" value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="Invoices printed on this date or later" />
          </label>
          <label className="table-date-field">
            <span>To</span>
            <input type="date" className="filter-select" value={dateTo} onChange={e => setDateTo(e.target.value)} title="Invoices printed on this date or earlier" />
          </label>
        </div>

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Plate Number</th>
                <th>Driver</th>
                <th>Helpers</th>
                <th>Checker</th>
                <th>Destination</th>
                <th>Items</th>
                <th>Total Amount</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40 }}>Loading...</td></tr>
              ) : groups.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40 }}>No delivery groups found</td></tr>
              ) : groups.map((g, i) => (
                <tr key={i}>
                  <td>{formatDate(g.dispatch_date)}</td>
                  <td style={{ fontWeight: 600 }}>{g.plate_number}</td>
                  <td>{g.driver || '-'}</td>
                  <td>{[g.helper_1, g.helper_2].filter(Boolean).join(', ') || '-'}</td>
                  <td>{g.checker_name || g.checker || '-'}</td>
                  <td>{g.destination || '-'}</td>
                  <td style={{ textAlign: 'center' }}>{g.item_count}</td>
                  <td className="amount">{formatCurrency(g.total_amount)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        className="btn btn-sm"
                        onClick={() => handlePrint(g.dispatch_date, g.plate_number)}
                        disabled={printLoading}
                        style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <FileDown size={14} /> PDF
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="pagination">
        <div className="pagination-info">
          Page {pagination.page} of {pagination.totalPages} ({pagination.total.toLocaleString()} records)
        </div>
        <div className="pagination-buttons">
          <button className="page-btn" disabled={pagination.page <= 1} onClick={() => loadGroups(1)}>First</button>
          <button className="page-btn" disabled={pagination.page <= 1} onClick={() => loadGroups(pagination.page - 1)}><ChevronLeft size={16} /></button>
          <button className="page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => loadGroups(pagination.page + 1)}><ChevronRight size={16} /></button>
          <button className="page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => loadGroups(pagination.totalPages)}>Last</button>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          .print-sheet, .print-sheet * { visibility: visible; }
          .print-sheet {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 20px !important;
            box-shadow: none !important;
          }
          .print-controls { display: none !important; }
          .print-overlay {
            position: absolute !important;
            background: white !important;
            padding: 0 !important;
          }
          @page {
            size: A4;
            margin: 15mm;
          }
        }
      `}</style>
    </div>
  );
}
